import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getDietOrder, uploadDietPdf, saveDietZalecenia, sendDietToPatient, getDietTemplates, saveAdminNote, cancelOrderKeepSlotByOrderId, restoreOrder, downloadDietOrderPdf } from '../api/dietitianApi';
import type { DietOrderDetail, DietTemplate } from '../types/dietitian.types';
import { Button } from '../../cabinet/components/ui/Button';
import { toast } from '../../cabinet/components/ui/Toast';

interface OrderDetailPageProps {
  orderId: number;
  onBack: () => void;
  isAdmin?: boolean;  // НОВЕ: true — адмін може редагувати нотатку, false — дієтолог тільки читає
}

const SURVEY_LABEL_MAP: Record<string, string> = {
  waga: 'Waga (kg)',
  diet_waga: 'Waga (kg)',
  wzrost: 'Wzrost (cm)',
  diet_wzrost: 'Wzrost (cm)',
  alergie: 'Alergie i nietolerancje',
  diet_alergie: 'Alergie i nietolerancje',
  allergies: 'Alergie i nietolerancje',
  schorzenia: 'Schorzenia do uwzględnienia',
  diet_schorzenia: 'Schorzenia do uwzględnienia',
  styl: 'Sposób odżywiania',
  diet_styl: 'Sposób odżywiania',
  nie_chce: 'Czego nie chce w jadłospisie',
  diet_nie_chce: 'Czego nie chce w jadłospisie',
  aktywnosc: 'Aktywność fizyczna',
  diet_aktywnosc: 'Aktywność fizyczna',
  posilki: 'Ilość posiłków',
  diet_posilki: 'Ilość posiłków',
  osoby: 'Dla ilu osób gotuje',
  diet_osoby: 'Dla ilu osób gotuje',
  czas: 'Czas na gotowanie',
  diet_czas: 'Czas na gotowanie',
  sprzet: 'Sprzęt kuchenny',
  diet_sprzet: 'Sprzęt kuchenny',
  ulubione: 'Ulubione potrawy',
  diet_ulubione: 'Ulubione potrawy',
  uwagi: 'Dodatkowe informacje',
  diet_uwagi: 'Dodatkowe informacje',
};

const formatSurveyKey = (key: string): string => {
  return SURVEY_LABEL_MAP[key] || SURVEY_LABEL_MAP[key.replace('diet_', '')] || key.replace('diet_', '').replace(/_/g, ' ');
};

const formatSurveyVal = (key: string, val: any): string => {
  if (Array.isArray(val)) return val.join(', ');
  if (val === '' || val === null || val === undefined) return '—';
  const cleanKey = key.replace('diet_', '');
  const strVal = String(val).trim();
  if (cleanKey === 'waga' && !strVal.toLowerCase().includes('kg')) {
    return `${strVal} kg`;
  }
  if (cleanKey === 'wzrost' && !strVal.toLowerCase().includes('cm')) {
    return `${strVal} cm`;
  }
  return strVal;
};

export const OrderDetailPage: React.FC<OrderDetailPageProps> = ({ orderId, onBack, isAdmin = false }) => {
  const [order, setOrder] = useState<DietOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<DietTemplate[]>([]);

  const [zalecenia, setZalecenia] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [savingZalecenia, setSavingZalecenia] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [data, tpls] = await Promise.all([
        getDietOrder(orderId),
        getDietTemplates(),
      ]);
      setOrder(data);
      setZalecenia(data.zalecenia || data.zalecenia_draft || '');
      setAdminNote(data.admin_note || '');
      setTemplates(tpls.templates);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const handlePdfUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfUploading(true);
    try {
      const res = await uploadDietPdf(orderId, file);
      setOrder(prev => prev ? { ...prev, pdf_url: res.pdf_url, has_pdf: true } : null);
      toast('PDF завантажено успішно!' + (res.medyk_sync ? ' (Синхронізовано з MEDYK)' : ''));
    } catch (err: any) {
      toast('Помилка: ' + (err.response?.data?.error || err.message));
    } finally {
      setPdfUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveZalecenia = async (draft = true) => {
    setSavingZalecenia(true);
    try {
      await saveDietZalecenia(orderId, zalecenia, draft);
      setOrder(prev => prev ? { 
        ...prev, 
        zalecenia_draft: draft ? zalecenia : null,
        zalecenia: !draft ? zalecenia : prev.zalecenia
      } : null);
      if (draft) toast('Zapisano jako draft (szkic).');
    } catch (err) {
      toast('Błąd zapisu');
    } finally {
      setSavingZalecenia(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm('Wysłać do pacjenta? Zmieni to status na "Gotowe".')) return;
    setSending(true);
    try {
      await saveDietZalecenia(orderId, zalecenia, false);
      const res = await sendDietToPatient(orderId);
      setOrder(prev => prev ? { ...prev, status: res.status as any, status_label: 'Gotowe' } : null);
      toast('Wysłano pomyślnie!');
      onBack();
    } catch (err: any) {
      toast('Помилка: ' + (err.response?.data?.error || err.message));
    } finally {
      setSending(false);
    }
  };

  // [НОВЕ] Збереження нотатки адміна
  const handleSaveAdminNote = async () => {
    if (!isAdmin) return;
    setSavingNote(true);
    try {
      await saveAdminNote(orderId, adminNote);
      setOrder(prev => prev ? { ...prev, admin_note: adminNote } : null);
    } catch (err: any) {
      toast('Błąd zapisu notatki: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingNote(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Czy na pewno chcesz anulować wizytę i zwolnić termin dla tego zamówienia?')) return;
    setCanceling(true);
    try {
      await cancelOrderKeepSlotByOrderId(orderId);
      setOrder(prev => prev ? { ...prev, status: 'cancelled' as any, status_label: 'Anulowane' } : null);
      toast('Wizyta anulowana, termin zwolniony.');
    } catch (err: any) {
      toast('Błąd podczas anulowania: ' + (err.response?.data?.error || err.message));
    } finally {
      setCanceling(false);
    }
  };

  const handleRestoreOrder = async () => {
    if (!window.confirm('Czy na pewno chcesz przywrócić to zamówienie do realizacji?')) return;
    setRestoring(true);
    try {
      await restoreOrder(orderId);
      setOrder(prev => prev ? { ...prev, status: 'wc-diet-confirmed' as any, status_label: 'W przygotowaniu' } : null);
      toast('Zamówienie przywrócone.');
    } catch (err: any) {
      toast('Błąd podczas przywracania: ' + (err.response?.data?.error || err.message));
    } finally {
      setRestoring(false);
    }
  };

  const isUserAdmin = Boolean(
    isAdmin ||
    (window as any).dietitianVars?.userRole === 'administrator' ||
    (window as any).dietitianVars?.userRole === 'admin'
  );

  const handleDownloadPdf = async () => {
    if (!order?.pdf_url) return;
    setDownloadingPdf(true);
    try {
      const sanitizedName = order.customer_name ? order.customer_name.trim().replace(/[^a-zA-Z0-9ąćęłńóśźżĄĆĘŁŃÓŚŹŻ_-]/g, '_') : '';
      const fileName = sanitizedName ? `jadlospis_${sanitizedName}_${order.id}.pdf` : `jadlospis_${order.id}.pdf`;
      await downloadDietOrderPdf(order.id, fileName);
      toast('Pobrano plik PDF');
    } catch (err: any) {
      toast('Błąd podczas pobierania pliku PDF: ' + (err.response?.data?.error || err.message || 'Nieznany błąd'));
    } finally {
      setDownloadingPdf(false);
    }
  };

  const insertTemplate = (content: string) => {
    setZalecenia(prev => prev ? prev + content : content);
  };

  if (loading || !order) return <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Ładowanie...</div>;

  const isDone = order.status === 'wc-diet-done';
  const isCancelled = order.status === 'wc-cancelled' || order.status === 'cancelled';

  return (
    <div>
      <span className="back" onClick={onBack}>← Wróć do zleceń</span>
      
      <div className="phead">
        <div className="av">{order.customer_name.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1 }}>
          <div className="nm">{order.customer_name}</div>
          <div className="sub">
            <span>{order.customer_email}</span>
            <span> · {order.customer_phone}</span>
            {order.customer_dob && order.customer_dob !== 'Nieznana' && <span> · {order.customer_dob}</span>}
            {order.customer_gender && order.customer_gender !== 'Nieznana' && <span> · {order.customer_gender}</span>}
            {(order.waga || order.survey?.diet_waga) && (
              <span> · {order.waga || order.survey.diet_waga}{!String(order.waga || order.survey.diet_waga).toLowerCase().includes('kg') ? ' kg' : ''}</span>
            )}
            {(order.wzrost || order.survey?.diet_wzrost) && (
              <span> · {order.wzrost || order.survey.diet_wzrost}{!String(order.wzrost || order.survey.diet_wzrost).toLowerCase().includes('cm') ? ' cm' : ''}</span>
            )}
            <span> · {order.ordered_at}</span>
          </div>
        </div>
        <span className="ttag">{order.type_label}</span>
      </div>

      <div className="cols">
        {/* L: Ankieta */}
        <div>
          <div className="card">
            <h3>Odpowiedzi z ankiety</h3>
            {Object.entries(order.survey).map(([key, val]) => (
              <div className="kv" key={key}>
                <span className="k">{formatSurveyKey(key)}</span>
                <span className="v">{formatSurveyVal(key, val)}</span>
              </div>
            ))}
            {order.patient_question && (
              <div className="kv" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                <span className="k" style={{ color: 'var(--v900)', fontWeight: 600 }}>Pytanie pacjenta</span>
                <span className="v" style={{ textAlign: 'left', fontWeight: 400 }}>{order.patient_question}</span>
              </div>
            )}
          </div>

          {/* Нотатка адміна — замість doctor_recommendations з MEDYK */}
          <div className="card">
            <h3>Notatka o pacjentce{' '}
              <span style={{ fontWeight: 400, color: 'var(--v600)', fontSize: 12 }}>
                {isAdmin ? '(edytujesz jako admin)' : '(od zespołu Tabletka24)'}
              </span>
            </h3>
            <textarea
              className="editor"
              value={adminNote}
              onChange={isAdmin ? (e) => setAdminNote(e.target.value) : undefined}
              readOnly={!isAdmin}
              placeholder={isAdmin
                ? 'Wpisz notatkę dla dietetyka (np. specyfika pacjentki, leki, uwagi)...'
                : 'Brak notatki od administratora.'
              }
              style={{
                width: '100%',
                resize: 'vertical',
                minHeight: 80,
                background: isAdmin ? undefined : '#f9f7fd',
                cursor: isAdmin ? undefined : 'default',
              }}
            />
            {isAdmin && (
              <div className="actions" style={{ marginTop: 8 }}>
                <button className="btn sm" onClick={handleSaveAdminNote} disabled={savingNote}>
                  {savingNote ? 'Zapisywanie...' : 'Zapisz notatkę'}
                </button>
                <span className="note" style={{ margin: 0 }}>Dietetyk widzi tę notatkę jako tylko do odczytu.</span>
              </div>
            )}
          </div>
        </div>

        {/* R: Akcje (PDF, Zalecenia, Meet) */}
        <div>
          {(order.type === 'konsultacja' || order.type === 'pakiet') && (
            <div className="card">
              <h3>📹 Konsultacja</h3>
              {order.slot ? (
                <>
                  <div className="zaltxt" style={{ marginBottom: 12 }}>
                    <strong>Data:</strong> {order.slot_confirmed || order.slot}
                  </div>
                  {order.meet_link && order.meet_link.startsWith('http') ? (
                    <button className="btn meet" onClick={() => window.open(order.meet_link as string, '_blank')}>
                      Dołącz do Google Meet
                    </button>
                  ) : (
                    <div style={{ color: 'red', fontSize: 13.5 }}>Brak prawidłowego linku Meet (wartość: {order.meet_link || 'brak'})</div>
                  )}
                </>
              ) : (
                <div className="zaltxt">Pacjent nie wybrał terminu.</div>
              )}
            </div>
          )}

          <div className="card">
            <h3>Zalecenia dietetyka</h3>
            
            {!isDone && templates.length > 0 && (
              <div className="tplbar">
                <span style={{ fontSize: 12.5, color: 'var(--v600)', fontWeight: 600 }}>Wstaw szablon:</span>
                {templates.map(t => (
                  <span key={t.id} className="tplchip" onClick={() => insertTemplate(t.content)}>{t.name}</span>
                ))}
              </div>
            )}
            
            <div style={{ marginBottom: 50, background: isDone ? '#f9f7fd' : '#fff' }}>
              <ReactQuill
                theme="snow"
                value={zalecenia}
                onChange={setZalecenia}
                placeholder="Wpisz zalecenia dla pacjenta..."
                readOnly={isDone}
                style={{ height: 250 }}
              />
            </div>
            <div className="note">Ten tekst trafi do pacjentki (konto + e-mail) razem z jadłospisem.</div>
          </div>

          {(order.type === 'jadlospis' || order.type === 'pakiet' || Boolean(order.pdf_url)) && (
            <div className="card">
              <h3>Załącz jadłospis (PDF)</h3>
              
              {order.pdf_url ? (
                <>
                  <div className="file">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      📄 {order.pdf_url.split('/').pop()} ✓
                    </span>
                    {isUserAdmin && (
                      <button
                        type="button"
                        className="file-download-btn"
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        title="Pobierz plik PDF na dysk"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        {downloadingPdf ? 'Pobieranie...' : 'Pobierz PDF'}
                      </button>
                    )}
                  </div>
                  {!isDone && (
                    <div style={{ textAlign: 'center', marginTop: 10 }}>
                      <span className="link" onClick={() => fileInputRef.current?.click()}>Zmień plik PDF</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="drop" onClick={() => fileInputRef.current?.click()}>
                  {pdfUploading ? 'Wgrywanie...' : '⬆️ Kliknij, aby wybrać plik PDF'}
                </div>
              )}
              <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept=".pdf" onChange={handlePdfUpload} />
            </div>
          )}
          
          <div className="card" style={{ marginTop: 16 }}>
            <div className="actions" style={{ marginTop: 0 }}>
              {!isDone && !isCancelled && (
                <button 
                  className="btn" 
                  onClick={handleSend} 
                  disabled={sending || (!order.pdf_url && order.type !== 'konsultacja') && !zalecenia}
                >
                  {sending ? 'Wysyłanie...' : 'Wyślij do pacjentki'}
                </button>
              )}
              {isCancelled && isAdmin && (
                <button 
                  className="btn" 
                  onClick={handleRestoreOrder} 
                  disabled={restoring}
                  style={{ background: '#137333' }}
                >
                  {restoring ? 'Przywracanie...' : 'Przywróć do realizacji'}
                </button>
              )}
              {!isDone && !isCancelled && isAdmin && (
                <button 
                  className="btn ghost" 
                  onClick={handleCancelOrder} 
                  disabled={canceling}
                  style={{ color: '#d93025', borderColor: '#d93025' }}
                >
                  {canceling ? 'Anulowanie...' : 'Anuluj wizytę (zwolnij termin)'}
                </button>
              )}
              <span className="note" style={{ margin: 0, width: '100%' }}>
                {!isCancelled ? 'Wysyłka = konto pacjentki + e-mail, status → „Gotowe".' : 'Zamówienie jest anulowane.'}
              </span>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};
